import { useFollowedShows } from '../hooks/useShows'
import { ShowCard } from '../components/ShowCard'
import { AuthGuard } from '../components/AuthGuard'
import { PageHeader } from '../components/PageHeader'
import { LoadingState } from '../components/LoadingState'
import { ErrorState } from '../components/ErrorState'
import { FilterInput } from '../components/FilterInput'
import { computed, useSignal } from '@preact/signals'
import { fuzzyMatch } from '../utils/fuzzySearch'
import { compareShowNames } from '../utils/sortShows'

export function AllShows() {
  const filterText = useSignal('')

  const followedShowsQuery = useFollowedShows()

  const sortedShows = computed(() => {
    if (!followedShowsQuery.isSuccess) return []
    return [...followedShowsQuery.data].sort((a, b) =>
      compareShowNames(a._embedded.show.name, b._embedded.show.name),
    )
  })

  const filteredShows = computed(() => {
    const filter = filterText.value.trim()
    if (!filter) return sortedShows.value
    return sortedShows.value.filter(item => fuzzyMatch(filter, item._embedded.show.name))
  })

  if (followedShowsQuery.isPending) {
    return <LoadingState message="Loading shows..." />
  }

  if (followedShowsQuery.isError) {
    return <ErrorState message="Error loading shows" />
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-slate-900">
        <PageHeader
          title="All Shows"
          showLogout={true}
          navigationTo="/shows"
          navigationLabel="Recent"
        />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <FilterInput
              value={filterText}
              placeholder="Filter shows (e.g. 'bro gir' for '2 Broke Girls')..."
            />
          </div>

          {followedShowsQuery.data.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400 text-lg">No shows followed yet</p>
              <p className="text-slate-500 mt-2">
                Visit{' '}
                <a
                  href="https://www.tvmaze.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300"
                >
                  TVMaze.com
                </a>{' '}
                to follow shows
              </p>
            </div>
          ) : filteredShows.value.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400 text-lg">No shows match "{filterText.value}"</p>
              <button
                onClick={() => (filterText.value = '')}
                className="mt-4 text-blue-400 hover:text-blue-300 underline"
              >
                Clear filter
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredShows.value.map(
                item => item && <ShowCard key={item.show_id} show={item._embedded.show} />,
              )}
            </div>
          )}
        </main>
      </div>
    </AuthGuard>
  )
}
